import { Inject, Injectable } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { LocalStorageService } from 'ngx-webstorage'
import { CookieService } from '../../../singleton-services/cookie.service'
import { filter, first, map } from 'rxjs/operators'
import { Observable } from 'rxjs'
import { LoggerService } from 'src/app/logger/logger.interface'
import { LOGGER_SERVICE } from 'src/app/logger/logger.config'

/** Lifetime of the HasOffers cookie in days */
const COOKIE_LIFETIME = 30

/**
 * Manages marketing attribution tracking for affiliate platforms and referrer sources.
 *
 * Originally designed for HasOffers/TUNE (hence the name), extended to handle:
 * - HasOffers/TUNE transaction IDs (tid, transaction_id params)
 * - Original referrer tracking (original_ref param) - CXP-10191
 *
 * Uses cookie + localStorage dual storage to circumvent ITP 2.2 tracking prevention.
 */
@Injectable()
export class HasOffersService {
  public readonly STORAGE_NAME = 'flo_has_offers'
  private readonly REFERRER_STORAGE_NAME = 'flo_original_referrer'

  constructor(
    private readonly cookieService: CookieService,
    private readonly localStorageService: LocalStorageService,
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  /** Stores a HasOffers transaction ID from a link decoration */
  public storeTransactionId(): Observable<string> {
    // Parse query params from current URL - works globally regardless of which route user lands on
    const queryParams = this.router.parseUrl(this.router.url).queryParams
    const tid = queryParams.tid || queryParams.transaction_id

    if (!!tid) {
      this.logger.info('Storing affiliate referral link for HasOffer.', {
        'transaction id': tid
      })
      this.cookieService.set(this.STORAGE_NAME, tid, {
        expires: COOKIE_LIFETIME
      })
      this.localStorageService.store(this.STORAGE_NAME, tid)
    }

    this.restoreCookie()

    // Return Observable for backward compatibility with existing subscription
    return this.route.queryParams.pipe(
      first(),
      map(() => tid),
      filter<string>(Boolean)
    )
  }

  /** Stores the original referrer from Webflow redirect for marketing attribution */
  public storeOriginalReferrer(): Observable<string> {
    // Parse query params from current URL - works globally regardless of which route user lands on
    const queryParams = this.router.parseUrl(this.router.url).queryParams
    const ref = queryParams.original_ref

    if (ref) {
      // Validate URL format to prevent XSS
      try {
        const _ = new URL(ref)
        this.logger.info('Storing original referrer for marketing attribution.', {
          'original referrer': ref
        })
        this.cookieService.set(this.REFERRER_STORAGE_NAME, ref, {
          expires: COOKIE_LIFETIME,
          sameSite: 'Lax'
        })
        this.localStorageService.store(this.REFERRER_STORAGE_NAME, ref)
      } catch {
        this.logger.warn('Invalid original_ref URL format, ignoring.', { original_ref: ref })
      }
    }

    this.restoreReferrerCookie()

    // Return Observable for backward compatibility with existing subscription
    return this.route.queryParams.pipe(
      first(),
      map(() => ref),
      filter<string>(Boolean)
    )
  }

  /** Restores the original referrer cookie from local storage to circumvent ITP 2.2 */
  private restoreReferrerCookie(): void {
    const cookieExists = !!this.cookieService.get(this.REFERRER_STORAGE_NAME)
    if (cookieExists) return

    const ref = this.localStorageService.retrieve(this.REFERRER_STORAGE_NAME)
    if (!ref) return
    this.logger.info('Original referrer was restored from local storage.', { 'original referrer': ref })
    this.cookieService.set(this.REFERRER_STORAGE_NAME, ref, {
      expires: COOKIE_LIFETIME,
      sameSite: 'Lax'
    })
  }

  /** Obtains the original referrer from the cookie */
  public retrieveOriginalReferrer(): string | undefined {
    return this.cookieService.get(this.REFERRER_STORAGE_NAME)
  }

  /** Restores the HasOffers cookie from local storage to circumvent ITP 2.2 */
  private restoreCookie(): void {
    const cookieExists = !!this.cookieService.get(this.STORAGE_NAME)
    if (cookieExists) return

    const tid = this.localStorageService.retrieve(this.STORAGE_NAME)
    if (!tid) return
    this.logger.info('HasOffer transaction ID was restored from local storage.', { 'transaction id': tid })
    this.cookieService.set(this.STORAGE_NAME, tid, {
      expires: COOKIE_LIFETIME
    })
  }

  /** Obtains the transaction ID from the cookie */
  public retrieveTransactionId(): string | undefined {
    return this.cookieService.get(this.STORAGE_NAME)
  }
}
